/**
 * asset-schema.ts — ทรัพย์สินถาวร/ค่าเสื่อม/การจำหน่าย (CONTEXT §5.7, T066)
 *
 * กติกา:
 * - ซื้ออุปกรณ์ที่ classification ไม่ชัด → review_status='needs_review' (ห้ามเดา)
 *   depreciation กระทบ P&L เฉพาะ asset ที่ approved แล้ว (review_status='ok')
 * - depreciable base = cost + related_costs − salvage_value
 * - book value = (cost + related_costs) − accumulated depreciation (ปลายทาง = salvage)
 * - gain/loss จำหน่าย = proceeds − book value ณ วันจำหน่าย
 * - คำนวณด้วย Decimal (ห้าม float)
 */
import { z } from 'zod';
import { moneyString, uuid, dateKey } from './schemas.js';
import { toDecimal, quantizeMoney, moneyToString, type Decimal } from './money.js';

export const depreciationMethod = z.enum(['straight_line', 'declining_balance', 'none']);
export type DepreciationMethod = z.infer<typeof depreciationMethod>;

export const fixedAssetInput = z.object({
  name: z.string().trim().min(1).max(300),
  assetNo: z.string().trim().max(120).optional(),
  category: z.string().trim().max(120).optional(),
  purchaseDate: dateKey.optional(),
  readyDate: dateKey.optional(), // วันที่พร้อมใช้งาน (เริ่มคิดค่าเสื่อม)
  cost: moneyString,
  vatAmount: moneyString.optional(),
  relatedCosts: moneyString.optional(),
  usefulLifeMonths: z.coerce.number().int().min(1).max(1200).optional(),
  salvageValue: moneyString.optional(),
  method: depreciationMethod.optional(),
  location: z.string().trim().max(200).optional(),
  responsiblePerson: z.string().trim().max(200).optional(),
  /** true = ยืนยันเป็นทรัพย์สิน (review_status='ok'); default false = needs_review */
  approved: z.boolean().optional(),
  reason: z.string().trim().max(1000).optional(),
});
export type FixedAssetInput = z.infer<typeof fixedAssetInput>;

export const fixedAssetPatch = fixedAssetInput.partial();
export type FixedAssetPatch = z.infer<typeof fixedAssetPatch>;

export const assetDisposalInput = z.object({
  assetId: uuid,
  disposalDate: dateKey.optional(),
  proceeds: moneyString.optional(),
  bookValueAtDisposal: moneyString,
  method: z.enum(['sale', 'scrap', 'donate']).optional(),
  notes: z.string().trim().max(1000).optional(),
});
export type AssetDisposalInput = z.infer<typeof assetDisposalInput>;

/** ฐานคิดค่าเสื่อม = cost + related − salvage */
export function depreciableBase(
  cost: string,
  relatedCosts: string | undefined,
  salvageValue: string | undefined,
): string {
  return moneyToString(
    toDecimal(cost).plus(toDecimal(relatedCosts ?? '0')).minus(toDecimal(salvageValue ?? '0')),
  );
}

export interface DepreciationEntry {
  periodYear: number;
  periodMonth: number;
  amount: string;
  accumulated: string;
  bookValue: string;
}

function addMonths(year: number, month: number, i: number): { year: number; month: number } {
  const zero = (year * 12 + (month - 1)) + i;
  return { year: Math.floor(zero / 12), month: (zero % 12) + 1 };
}

/**
 * สร้างตารางค่าเสื่อมรายเดือน
 * - straight_line: เท่ากันทุกงวด งวดสุดท้ายซับ rounding ให้ accumulated = base พอดี
 * - declining_balance: double-declining (rate=2/n) บน book value, floor ที่ salvage, cap n งวด
 * - none: []
 */
export function computeDepreciationSchedule(asset: {
  cost: string;
  relatedCosts?: string | undefined;
  salvageValue?: string | undefined;
  usefulLifeMonths?: number | undefined;
  method?: DepreciationMethod | undefined;
  readyDate?: string | undefined;
  purchaseDate?: string | undefined;
}): DepreciationEntry[] {
  const method = asset.method ?? 'straight_line';
  const n = asset.usefulLifeMonths ?? 0;
  if (method === 'none' || n <= 0) return [];

  const grossBase = toDecimal(asset.cost).plus(toDecimal(asset.relatedCosts ?? '0'));
  const salvage = toDecimal(asset.salvageValue ?? '0');
  const base = grossBase.minus(salvage); // depreciable
  if (base.lte(0)) return [];

  const start = (asset.readyDate ?? asset.purchaseDate ?? '').split('-');
  if (start.length < 2 || !start[0] || !start[1]) return [];
  const startYear = Number(start[0]);
  const startMonth = Number(start[1]);

  const entries: DepreciationEntry[] = [];
  let accumulated = toDecimal('0');

  if (method === 'straight_line') {
    const monthly = quantizeMoney(base.div(n));
    for (let i = 0; i < n; i++) {
      const amount = i === n - 1 ? base.minus(monthly.times(n - 1)) : monthly;
      accumulated = accumulated.plus(amount);
      const { year, month } = addMonths(startYear, startMonth, i);
      entries.push({
        periodYear: year,
        periodMonth: month,
        amount: moneyToString(amount),
        accumulated: moneyToString(accumulated),
        bookValue: moneyToString(grossBase.minus(accumulated)),
      });
    }
    return entries;
  }

  // declining_balance (double declining)
  const rate = toDecimal(2).div(n);
  let bookValue: Decimal = grossBase;
  for (let i = 0; i < n; i++) {
    const remaining = bookValue.minus(salvage);
    if (remaining.lte(0)) break;
    let amount = quantizeMoney(bookValue.times(rate));
    if (i === n - 1 || amount.gte(remaining)) amount = remaining; // งวดท้าย/เกิน → เหลือถึง salvage
    accumulated = accumulated.plus(amount);
    bookValue = bookValue.minus(amount);
    const { year, month } = addMonths(startYear, startMonth, i);
    entries.push({
      periodYear: year,
      periodMonth: month,
      amount: moneyToString(amount),
      accumulated: moneyToString(accumulated),
      bookValue: moneyToString(bookValue),
    });
  }
  return entries;
}

/** gain/loss จำหน่าย = proceeds − book value */
export function computeDisposalGainLoss(
  proceeds: string | undefined,
  bookValueAtDisposal: string,
): string {
  return moneyToString(toDecimal(proceeds ?? '0').minus(toDecimal(bookValueAtDisposal)));
}
